<template>
  <div class="layout-regions">
    <MobileHeader
      :class="{ hide: showRegions }"
    >
      <template #action>
        <OperationTypeNavigationMobile :showHmePolicy="currentShaft?.policy?.show_heavy_mobile_equipment" />
      </template>
      <div class="menu">
        <ul class="menu__items">
          <li class="menu-item">
            <button
              :aria-label="$t('regions.search_regions_and_issues')"
              class="menu-item__link menu-item__link--icon"
              :disabled="selectedOperationId && !operationRegionTree"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
        </ul>
      </div>
    </MobileHeader>

    <div
      :class="{
        'layout-regions__header hide-for-medium': true,
        hide: showRegions,
      }"
    >
      <button
        class="select__trigger typo-ignore typo-title"
        type="button"
        @click="showRegions = true"
      >
        <div class="select__label-wrapper">
          <PolygonSvg
            v-if="selectedOperationRegion"
            :bounds="selectedOperationRegion.bounds"
            :class="`region-item__thumbnail region-item__thumbnail--no-margin region-item__thumbnail--${selectedOperationRegion.region_type}`"
            :project="false"
            :size="20"
          />
          <span class="select__label">
            {{
              selectedOperationRegion
                ? selectedOperationRegion.name
                : $t('regions.all_regions')
            }}
          </span>
        </div>
        <div class="select__arrow">
          <UseSvg
            class="icon icon--small"
            name="arrow_down_16"
          />
        </div>
      </button>
      <SwitchButton
        v-model="operationView"
        :items="operationViewOptions"
        @update:model-value="refreshView"
      />
    </div>

    <div
      :class="{
        'layout-regions__regions': true,
        'show-for-medium': !showRegions,
      }"
    >
      <OperationTypeNavigation :showHmePolicy="currentShaft?.policy?.show_heavy_mobile_equipment" />
      <div class="layout-regions__regions-header hide-for-medium">
        <button
          type="button"
          @click="showRegions = false"
        >
          <UseSvg
            class="icon"
            name="close"
          />
        </button>
        <span class="layout-regions__regions-title">
          {{ $t('regions.select_region') }}
        </span>
      </div>

      <div class="layout-regions__search-bar show-for-medium">
        <UseSvg
          :class="{ icon: true, 'icon--darker': !operationRegionSearch }"
          name="search"
        />
        <input
          v-model="operationRegionSearch"
          class="layout-regions__search-input typo-small-title"
          :disabled="!selectedOperationId || !operationRegionTree"
          :placeholder="$t('regions.search_all_regions')"
        >
        <button
          v-if="operationRegionSearch"
          class="button button--tiny button--transparent"
          type="button"
          @click="operationRegionSearch = ''"
        >
          <UseSvg
            class="icon icon--small"
            name="close_circle_16"
          />
        </button>
      </div>

      <!-- HME group type: START -->
      <FormRow
        v-if="shaft"
        :label="$t('shafts.group.heavy_mobile_equipment.select_equipment_type')"
        :modifiers="['narrow']"
      >
        <div class="form-row__input">
          <SkeletonElement
            v-if="shaft && loadingOperationTypes"
            :height="40"
            :radius="6"
          />
          <Select
            v-if="!loadingOperationTypes && operationType"
            ref="operationTypeSelect"
            v-model="selectedOperationTypeId"
            :aria-label="$t('shafts.group.heavy_mobile_equipment.select_equipment_type')"
            :items="operationType"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="$t('shafts.group.heavy_mobile_equipment.select_equipment_type')"
            :showTotalInLabel="false"
            @click.passive="checkScroll"
          />
        </div>
      </FormRow>
      <!-- HME group type: END -->

      <FormRow
        v-if="operationList"
        :label="$t('shafts.group.heavy_mobile_equipment.select_equipment')"
        :modifiers="['narrow']"
      >
        <div class="form-row__input">
          <SkeletonElement
            v-if="loadingOperations"
            :height="40"
            :radius="6"
          />
          <Select
            v-if="!loadingOperations && operationList"
            ref="operationSelect"
            v-model="selectedOperationId"
            :aria-label="$t('shafts.group.heavy_mobile_equipment.select_equipment')"
            :items="operationList"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="$t('shafts.group.heavy_mobile_equipment.select_equipment')"
            @click.passive="checkScroll"
          />
        </div>
      </FormRow>

      <RegionTree
        v-if="selectedOperationId && operationRegionTree && !loadingOperationRegions"
        :class="{ 'hide-for-medium': operationRegionSearch }"
        :modifiers="['manage-scroll']"
        :project="false"
        :regionTree="operationRegionTree"
        :selectedRegion="selectedOperationRegion"
        @select="selectRegion"
      />

      <Loader
        v-if="selectedOperationId && !operationRegionTree"
        class="show-for-medium"
        :modifiers="['height-fix']"
      />

      <EmptyMessage
        v-if="operationRegionSearch && operationRegionResults.length === 0"
        :message="$t('general.no_results')"
        :modifiers="['grow']"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <template v-if="operationRegionSearch && operationRegionResults.length > 0">
        <div class="layout-regions__results-header typo-small">
          {{ $t('general.results') }}
        </div>
        <div class="layout-regions__regions-tree scroll-container">
          <ul>
            <RegionItem
              v-for="region in operationRegionResults"
              :key="region.id"
              class="layout-regions__regions-tree"
              :ignoreSelected="true"
              :project="false"
              :region="region"
              :showChildren="false"
              @select="selectRegionFromSearch"
            />
          </ul>
        </div>
      </template>
    </div>

    <div
      :class="{
        'layout-regions__content': true,
        'show-for-medium': showRegions,
      }"
    >
      <!-- right start -->
      <div class="layout-regions__issues">
        <div class="layout-regions__search-bar show-for-medium">
          <UseSvg
            :class="{ icon: true, 'icon--darker': !operationIssueSearch }"
            name="search"
          />
          <input
            v-model="operationIssueSearch"
            class="layout-regions__search-input typo-small-title"
            :disabled="!operationIssueList || operationIssueList.length === 0"
            :placeholder="$t('regions.search_all_issues')"
          >
          <button
            v-if="operationIssueSearch"
            class="button button--tiny button--transparent"
            type="button"
            @click="operationIssueSearch = ''"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>

        <NoticeMessage
          v-if="issuesStore.loadingNotification && issuesStore.loading"
          iconStyle="info"
          :message="$t('general.long_load_message')"
          :modifiers="['margin']"
        />
        <IssuesSkeletonLoader
          v-if="selectedOperationId && issuesStore.loading"
          class="show-for-medium"
        />
        <div
          v-if="!issuesStore.loading"
          :class="{
            'layout-regions__issues-content': true,
            hide: !selectedOperationId || operationIssueSearch,
          }"
        >
          <div class="layout-regions__current-region show-for-medium">
            {{
              selectedOperationRegion
                ? selectedOperationRegion.name
                : $t('regions.all_regions')
            }}
          </div>
          <div class="layout-regions__issues-header">
            <span
              v-if="operationIssueList"
              :aria-label="$t('activerecord.models.plants/issue', { count: 0 })"
              class="layout-regions__issues-count typo-small-nav-item"
            >
              {{ operationIssueList.length }}
              {{ $t('activerecord.models.plants/issue', { count: operationIssueList.length }) }}
            </span>

            <IssueFilter
              v-if="isActiveFilter || operationIssueList?.length > 0"
              :sortOptions="sortByOptions"
              :typeFilter="false"
              @set-filter-options="setFilterOptions"
            />
          </div>

          <RecyclerView
            v-if="operationIssueList"
            ref="issueScrollView"
            :class="{
              'layout-regions__list': true,
              'show-for-medium': operationView !== 'list',
            }"
            :count="operationIssueList.length"
            :gap="0.125"
            :height="7"
            hideScrollingX
            name="layout-regions__list-item-"
          >
            <template #default="{ index, style, key }">
              <IssueCard
                :key="key"
                :isLocatable="true"
                isSmallCard
                :issue="operationIssueList[index]"
                overrideIssuePath="shaftV2RegionsIssuesTypeHme"
                :responsive="false"
                :selected="operationIssueList[index] === selectedOperationIssue"
                :showDescription="false"
                :style="style"
                @locate="selectIssueOnList(operationIssueList[index])"
              />
            </template>
          </RecyclerView>
        </div>

        <Loader
          v-if="selectedOperationId && !operationIssueList"
          class="show-for-medium"
        />
        <div
          v-if="selectedOperationId && operationIssueSearch"
          class="layout-regions__issues-content"
        >
          <EmptyMessage
            v-if="operationIssueSearch && operationIssueResults.length === 0"
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
          <template v-if="operationIssueSearch && operationIssueResults.length > 0">
            <div class="layout-regions__results-header typo-small">
              {{ $t('general.results') }}
            </div>
            <div class="layout-regions__list scroll-container">
              <ul>
                <IssueCard
                  v-for="issue in operationIssueResults"
                  :key="issue.id"
                  isSmallCard
                  :issue="issue"
                  noLink
                  overrideIssuePath="shaftV2RegionsIssuesTypeHme"
                  :responsive="false"
                  :showDescription="false"
                  @select="selectIssueFromSearch(issue)"
                />
              </ul>
            </div>
          </template>
        </div>
      </div>
      <!-- right end -->
      <!-- middle start -->
      <div
        :class="{
          'layout-regions__map layout-regions__map--center': true,
          'show-for-medium': operationView !== 'map',
        }"
      >
        <Loader
          v-if="issuesStore.loading"
          class="layout-regions__mobile-issues-loading hide-for-medium"
        />
        <LeafletMap
          v-if="loadMap && !loadingOperation && selectedOperation && !selectedOperation?.has_pending_tile_process"
          :key="issuesStore.componentKey"
          ref="map"
          :diagram="selectedOperation.diagram_identifier"
          @init="onMapInit"
          @marker-click="selectIssueOnMap"
          @polygon-click="selectRegionOnMap"
        />
        <EmptyMessage
          v-else-if="!map && selectedOperation?.has_pending_tile_process"
          :message="$t('shafts.group.heavy_mobile_equipment.diagram_loading_description')"
          :title="$t('shafts.group.heavy_mobile_equipment.diagram_loading_title')"
        />
        <EmptyMessage
          v-else
          :message="$t('shafts.group.heavy_mobile_equipment.no_hme_selection.message')"
          :title="$t('shafts.group.heavy_mobile_equipment.no_hme_selection.title')"
        >
          <NoMobileAssetsSvg />
        </EmptyMessage>
        <Loader
          v-if="loadingOperation"
          :modifiers="['overlay']"
        />
      </div>
      <!-- middle end -->
    </div>

    <div
      v-if="mobileSearchOpen"
      class="layout-regions__mobile-search hide-for-medium"
    >
      <div class="layout-regions__search-bar">
        <UseSvg
          class="icon"
          name="search"
        />
        <input
          ref="mobileSearchInput"
          v-model="mobileSearch"
          class="layout-regions__search-input typo-small-title"
          :placeholder="$t('regions.search_regions_and_issues')"
        >
        <UseSvg
          class="icon"
          name="close_circle_24"
          @click="mobileSearchOpen = false"
        />
      </div>
      <EmptyMessage
        v-if="mobileSearch && operationRegionResults.length === 0 && operationIssueResults.length === 0"
        :message="$t('general.no_results')"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <ul class="layout-regions__search-results scroll-container">
        <RegionItem
          v-for="region in operationRegionResults"
          :key="region.id"
          :ignoreSelected="true"
          :project="false"
          :region="region"
          :showChildren="false"
          @select="selectRegionFromSearch"
        />
        <IssueCard
          v-for="issue in operationIssueResults"
          :key="issue.id"
          isSmallCard
          :issue="issue"
          noLink
          overrideIssuePath="shaftV2RegionsIssuesTypeHme"
          :responsive="false"
          :showDescription="false"
          @select="selectIssueFromSearch(issue)"
        />
      </ul>
    </div>

    <Loader
      v-if="selectedOperationId && (!map || !operationRegionTree || !operationIssueList)"
      class="hide-for-medium"
      :modifiers="['overlay']"
    />
  </div>
  <router-view v-if="['shaftV2RegionsIssuesTypeHme', 'shaftV2IssuesLocationTypeHme'].includes($route.name)" />
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick, reactive } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useShaftV2 } from '@/functions/use_remote_object';
import { sortingFunctions, filterBy } from '@/functions/utils.js';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import debounce from '@/vendor/debounce';
import Searcher from '@/functions/searcher';
import booleanContains from '@turf/boolean-contains';
import ancestry from '@/functions/ancestry';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { compareByAreaAndIdInverse } from '@/functions/compare_regions';
import { geoJSONPolygonToLatLngs, latLngToGeoJSON, rgeoPolygonToGeoJSON, rgeoToLatLng } from '@/functions/geospatial_conversions';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import { useActiveFilter } from '@/functions/composables/active_filter.js';
import { filterBySelectedRegion } from '@/functions/filter_by_selected_region.js';

// Stores
import { useIssuesStore } from '@/stores/issues/issuesStore';

// Components
import MobileHeader from '@/components/generic/mobile_header.vue';
import RegionItem from '@/components/regions/region_item.vue';
import RegionTree from '@/components/regions/region_tree.vue';
import Select from '@/components/form/select.vue';
import FormRow from '@/components/form/row.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import Loader from '@/components/generic/loader.vue';
import IssueFilter from '@/components/issues/issue_filter.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import OperationTypeNavigation from '@/components/shaftsV2/operations/operation_type_navigation.vue';
import OperationTypeNavigationMobile from '@/components/shaftsV2/operations/operation_type_navigation_mobile.vue';
import IssuesSkeletonLoader from '@/components/issues/issues_skeleton_loader.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';

// Icons
import { DotIcon } from '@/vendor/leaflet_mods';
import NoMobileAssetsSvg from '@/images/no-mobile-assets.svg';
import PolygonSvg from '@/components/generic/polygon_svg.vue';

// Set up store
const issuesStore = useIssuesStore();

const IssueIcon = DotIcon.extend({
  options: {
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  },
});

const RISK_ICONS = {
  // @ts-ignore
  low: new IssueIcon({ className: 'issue-point issue-point--low-risk' }),
  // @ts-ignore
  medium: new IssueIcon({ className: 'issue-point issue-point--medium-risk' }),
  // @ts-ignore
  significant: new IssueIcon({ className: 'issue-point issue-point--significant-risk' }),
  // @ts-ignore
  high: new IssueIcon({ className: 'issue-point issue-point--high-risk' }),
};

const SELECTED_RISK_ICONS = Object.entries(RISK_ICONS).reduce((obj, [risk, icon]) => {
  // @ts-ignore
  obj[risk] = new IssueIcon({ className: icon.options.className + ' issue-point--selected' });
  return obj;
}, {});

const props = defineProps({
  issueType: { type: String, required: false, default: null },
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  heavyMobileEquipmentId: { type: String, required: false, default: null },
  inspectionId: { type: String, required: false, default: null },
  issueId: { type: String, required: false, default: null },
});

const ajax = useAjax();
const sortFunctions = sortingFunctions();
const router = useRouter();
const route = useRoute();
const currentShaft = useShaftV2({ organisationId: route.params.organisationId, shaftId: route.params.shaftId });
const shaft = ref(null);
const operationView = ref('map');
const view = ref('main');
const map = ref(null);

const mobileSearchOpen = ref(false);
const mobileSearch = ref('');
const mobileSearchInput = ref(null);
const operationRegionSearch = ref('');
const operationIssueSearch = ref('');
const operationRegionResults = ref([]);
const operationIssueResults = ref([]);

const sortBy = ref('');
const showRegions = ref(false);
const issueScrollView = ref(null);
const operationIssues = ref(null);
const operationRegionMap = ref(null);
const operationRegionTree = ref([]);
const operationIssueMap = ref(null);
const operationTypeSelect = ref();
const operationSelect = ref();
const selectedOperationRegion = ref(null);
const selectedOperationIssue = ref(null);

const conditions = ref([]);
const priorities = ref([]);

const operationType = ref(null);
const operationTypeList = ref(null);
const operationList = ref(null);
const operationRegions = ref(null);
const selectedOperationTypeId = ref(null);
const selectedOperationType = ref(null);
const selectedOperationId = ref(null);
const selectedOperation = ref(null);
const loadingOperation = ref(false);
const loadingOperations = ref(false);
const loadingOperationTypes = ref(false);
const loadingOperationRegions = ref(true);
const loadMap = ref(true);
const { isActiveFilter, validateFilterOptions } = useActiveFilter();
const routerNameList = ['shaftV2IssuesTypeHmes', 'shaftV2IssuesTypeHme'];

let operationRegionsSearcher = null;
let operationIssuesSearcher = null;

const sortByOptions = [
  { label: I18n.t('issues.highest_risk'), value: 'highest_risk'},
  { label: I18n.t('issues.lowest_risk'), value: 'lowest_risk'},
  { label: I18n.t('general.oldest'), value: 'oldest'},
  { label: I18n.t('general.newest'), value: 'newest'},
  { label: I18n.t('issues.highest_repair_priority'), value: 'highest_repair_priority'},
  { label: I18n.t('issues.lowest_repair_priority'), value: 'lowest_repair_priority'},
];

const ENDPOINTS = computed(() => {
  const shortBaseUrl = `/organisations/${props.organisationId}/shafts_v2`;
  const baseUrl = `${shortBaseUrl}/shafts/${props.shaftId}`;
  return {
    shaft: baseUrl,
    operations: `${baseUrl}/heavy_mobile_equipments`,
    regions: 'heavy_mobile_equipment_regions',
    operation_types: `${shortBaseUrl}/heavy_mobile_equipment_types`,
  };
});

onMounted(async () => {
  await loadShaft();
  await loadOperations();
  if (route.params?.heavyMobileEquipmentId && isIncluded(routerNameList, route.name)) {
    preselectFields(route.params.heavyMobileEquipmentId);
  }
});

onUnmounted(() => {
  ajax.abort(); // Abort any ajax calls when leaving this component
});

watch(route, () => {
  if (route.params?.heavyMobileEquipmentId && isIncluded(routerNameList, route.name)) {
    preselectFields(route.params.heavyMobileEquipmentId);
  }
});

watch(issuesStore, () => {
  renderIssues();
});

watch(() => props.issueType, async() => {
  deselectIssue();
  clearData();

  !shaft.value && await loadShaft();
  !operationTypeList.value && await loadOperations();
});

watch(selectedOperationTypeId, async(value) => operationList.value = await prepareOperationList(value));

watch(selectedOperationId, async(value) => {
  value && router.push({
    name: 'shaftV2IssuesTypeHme',
    params: { heavyMobileEquipmentId: value },
    query: route.query
  });
  deselectIssue();
  selectedOperation.value = await loadOperation(value);
  loadIssues(selectedOperationId.value);
});

watch(
  mobileSearch,
  debounce(
    () => {
      if (!mobileSearchOpen.value || !mobileSearch.value) return;
      operationRegionResults.value = operationRegionsSearcher.search(mobileSearch.value);
      operationIssueResults.value = operationIssuesSearcher.search(mobileSearch.value);
    }, 300, true
  )
);

/**
 * Reloads the map and update issues when filtering occurs
 * or a different diagram is selected
 */
watch(
  () => [selectedOperationId.value, sortBy.value, conditions.value, priorities.value],
  async () => {
    loadMap.value = false;
    await nextTick();
    loadMap.value = true;
  }
);

async function renderIssues() {
  operationIssues.value = issuesStore.issues;
  operationIssueMap.value = new Map();
  if (!map.value) return; // in case user navigates away

  const isActiveFilter = sortBy.value || conditions.value?.length > 0 || priorities.value?.length > 0;

  if (isActiveFilter) {
    const filteredIssues = filteredIssueList(issuesStore.issues);
    filteredIssues.forEach(loadIssue);
  } else {
    operationIssues.value.forEach(loadIssue);
  }

  operationIssuesSearcher = new Searcher(operationIssueMap.value, { keys: ['title', 'id'] });

  refreshView();

  await loadSelectedContent();
}

async function loadShaft() {
  if (props.shaftId) {
    let { ok, data } = await ajax(ENDPOINTS.value.shaft);

    if (ok) {
      shaft.value = data;
    }
  }
}

async function loadOperations() {
  operationList.value = null;
  if (route.params.shaftId) {
    loadingOperations.value = true;
    let { ok, data } = await ajax(ENDPOINTS.value.operations);

    if (ok) {
      operationTypeList.value = data;
      await loadOperationTypes();
      loadingOperations.value = false;
    }
  }
}

async function loadOperationTypes() {
  if (route.params.shaftId) {
    loadingOperationTypes.value = true;
    let { ok, data } = await ajax(ENDPOINTS.value.operation_types);

    if (ok) {
      operationType.value = data?.map((item) => ({
        label: item.name,
        value: item.id,
        total: `${operationTypeList.value[item.name]?.length || 0}`
      }));
      // Add extra types to the list
      let totalEquipment = 0;
      Object.entries(operationTypeList.value).forEach((group) => totalEquipment += group[1]?.length || 0);
      operationType.value.unshift(
        { label: I18n.t('shafts.configuration.heavy_mobile_equipment.view_all'), value: 'view_all', total: `${totalEquipment}` },
        { label: I18n.t('shafts.configuration.heavy_mobile_equipment.no_type'), value: 'heavy_mobile_equipment', total: `${operationTypeList.value['heavy_mobile_equipment']?.length || 0}` }
      );
      loadingOperationTypes.value = false;
    }
  }
}

async function loadOperation(operationId) {
  if (operationId) {
    loadingOperation.value = true;
    let { ok, data } = await ajax(`${ENDPOINTS.value.operations}/${operationId}`);

    if (ok) {
      selectedOperation.value = data;
      // TODO: find a proper way to handle the delay
      setTimeout(() => {
        loadingOperation.value = false;
      }, 0);
      // marker = null;
      return data;
    }
  }
}

function clearData() {
  mobileSearchOpen.value = false;
  mobileSearch.value = '';
  selectedOperationId.value = null;
  selectedOperation.value = null;
  operationRegionResults.value = [];
  operationIssueResults.value = [];
  operationRegionTree.value = [];
  loadingOperationRegions.value = true;
}

async function preselectFields(id) {
  // Preselect the type
  let foundHmeId = null;
  Object.keys(operationTypeList.value).forEach((key) => {
    const foundEquipment = Object.values(operationTypeList.value[key]).find(equipment => equipment.id === id);
    if (foundEquipment) {
      foundHmeId = foundEquipment?.heavy_mobile_equipment_type_id;
      foundHmeId && (selectedOperationTypeId.value = foundHmeId);
    } else if (!foundHmeId) {
      /**
       * If the HME id is false then the equipment has no type
       * 'heavy_mobile_equipment' is the group for equipments without a type
       */
      selectedOperationTypeId.value = 'heavy_mobile_equipment';
    }
  });
  await nextTick();
  // Preselect the operation
  selectedOperationId.value = id;
}

function prepareOperationList(typeId) {
  clearData();
  let operationListNew = [];

  if (typeId === 'view_all') {
    Object.entries(operationTypeList.value).forEach((group) => {
      operationListNew.push(...group[1]);
    });
  } else {
    selectedOperationType.value = operationType.value.find(type => type.value === typeId);
    const labelOrValue = typeId === 'heavy_mobile_equipment' ? 'value' : 'label';
    const selectedType = selectedOperationType.value && selectedOperationType.value[labelOrValue];
    operationListNew = operationTypeList.value[selectedType];
  }

  return operationListNew?.map((item) => ({
    label: item.name,
    value: item.id,
    diagram: item.diagram_identifier,
  })) || null;
}

async function selectIssueOnList(issue) {
  operationView.value = 'map';
  await refreshView();
  await selectIssue(issue);
}

async function selectIssueFromSearch(issue) {
  mobileSearchOpen.value = false;
  view.value = 'list';
  selectIssueOnList(issue);
}

async function selectRegionFromSearch(region) {
  mobileSearchOpen.value = false;
  operationRegionSearch.value = '';
  await selectRegion(region);
}

async function openMobileSearch() {
  mobileSearchOpen.value = true;
  mobileSearch.value = '';

  operationRegionResults.value = [];
  operationIssueResults.value = [];

  await nextTick();
  mobileSearchInput.value.focus();
}

function setFilterOptions(options) {
  sortBy.value = options.sortOption;
  conditions.value = options.conditions;
  priorities.value = options.priorities;

  /* Check if filter has any active filter options */
  validateFilterOptions(options);
}

watch(operationRegionSearch, debounce(() => {
  if (!operationRegionSearch.value) return;

  operationRegionResults.value = operationRegionsSearcher.search(operationRegionSearch.value);
}, 300, true));

watch(operationIssueSearch, debounce(() => {
  if (!operationIssueSearch.value) return;

  operationIssueResults.value = operationIssuesSearcher.search(operationIssueSearch.value);
}, 300, true));

const operationIssueList = computed(() =>
  selectedOperationRegion.value
    ? filterBySelectedRegion(filteredIssueList(), selectedOperationRegion.value)
    : filteredIssueList()
);

function filteredIssueList(passedIssues) {
  const issues = passedIssues || operationIssueMap?.value;

  if (!issues) return null;

  let list = [...issues.values()];

  if (conditions.value.length > 0) {
    list = list.filter(i => {
      return conditions.value.includes(i.overall_condition_rating);
    });
  }

  if (priorities.value.length > 0) {
    list = list.filter(i => {
      return priorities.value.includes(i.priority);
    });
  }

  if (sortBy.value === 'highest_repair_priority' || sortBy.value === 'lowest_repair_priority') {
    const highList = filterBy(list, 'priority', 'X');
    const lowList = filterBy(list, 'priority', 'M');
    const numberList = filterBy(list, 'priority')?.sort(sortFunctions[sortBy.value]);
    let finalList = [];

    if (sortBy.value === 'highest_repair_priority') {
      finalList = [...highList, ...numberList, ...lowList];
    } else if (sortBy.value === 'lowest_repair_priority') {
      finalList = [...lowList, ...numberList, ...highList];
    }
    return finalList;
  } else {
    return list.sort(sortFunctions[sortBy.value]);
  }
}

const operationViewOptions = [
  { icon: 'crosshair_dot', value: 'map' },
  { icon: 'list_bullet_end', value: 'list' },
];

async function loadRegions(operationId) {
  if (!props.shaftId || !operationId) return;
  loadingOperationRegions.value = true;
  let { ok, data } = await ajax(`${ENDPOINTS.value.operations}/${operationId}/heavy_mobile_equipment_regions`);

  if (ok) {
    operationRegions.value = data.regions;
    loadingOperationRegions.value = false;
    operationRegionMap.value = new Map();
    if (!map.value) return; // in case user navigates away

    operationRegions.value?.forEach(loadRegion);
    operationRegionTree.value = ancestry(operationRegionMap.value.values());
  }
}

async function loadIssues(operationId) {
  if (!props.shaftId || !operationId) return;
  issuesStore.$reset();

  operationIssues.value = [];
  operationIssueMap.value = new Map();

  setTimeout(() => {
    issuesStore.loadingNotification = true;
  }, 5000);

  let { ok, data } = await ajax.paginated(
    `${ENDPOINTS.value.operations}/${operationId}/issues`,
    { query: { category: 'approved_and_open' }, items: operationIssues.value }
  );

  if (ok) {
    issuesStore.issues = data;
  }
  issuesStore.setLoadingToFalse();
}

async function onMapInit() {
  await loadRegions(selectedOperationId.value);
  await renderIssues();
  await loadSelectedContent();
}

async function loadSelectedContent() {
  if (!map.value) return; // in case user navigates away

  if (route.query.regionId) {
    await selectRegion(operationRegionMap.value.get(route.query.regionId), { redirect: false, goTo: !route.query.issueId });
  }
  if (route.query.issueId) {
    await selectIssue(operationIssueMap.value.get(route.query.issueId), { redirect: false });
  }
}

function checkScroll() {
  if (!selectedOperationTypeId.value && !loadingOperationTypes.value && operationType.value) {
    operationTypeSelect.value.dropdownContainerRef.scrollTop = 0;
  }
  if (!selectedOperationId.value && !loadingOperations.value && operationList.value) {
    operationSelect.value.dropdownContainerRef.scrollTop = 0;
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(latLngs, regionPolygonOptions(region.region_type));
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.disableEdit();
  polygon.region = regionObject;

  operationRegionMap.value.set(region.id, reactive(regionObject));

  operationRegionsSearcher = new Searcher(operationRegionMap.value);
}

function loadIssue(issue) {
  let latLng = rgeoToLatLng(issue.location.location);
  let geoJSON = latLngToGeoJSON(latLng);
  let riskLevel = getRiskRatingLevel(issue.risk_rating);
  let zIndexOffset = issue.risk_rating * 10;
  let marker = map.value.addMarker(latLng, {
    icon: RISK_ICONS[riskLevel],
    riseOnHover: true,
    zIndexOffset,
  });
  let issueObject = {
    ...issue,
    timestamp: +new Date(issue.inspection.date),
    riskLevel,
    latLng,
    geoJSON,
    marker,
    zIndexOffset,
  };
  marker.issue = issueObject;

  operationIssueMap.value.set(issue.id, issueObject);

  if (issue.id === route.query.issueId) map.value.enableCrosshair(marker._latlng.lat, marker._latlng.lng);
}

async function refreshView() {
  await nextTick();

  switch (operationView.value) {
    case 'map':
      if (map.value) await map.value.refresh();
      break;
    case 'list':
      issueScrollView.value?.update();
      scrollToIssue(selectedOperationIssue.value, { behavior: 'auto', block: 'top', force: true });
      break;
  }
}

// make sure clicking on a stack of objects doesn't trigger a click on all of them
let selectingObject = false;
function doSelect() {
  selectingObject = true;
  setTimeout(() => selectingObject = false, 0);
}

function deselectRegion() {
  if (selectedOperationRegion.value) {
    const previousRegion = operationRegionMap.value.get(selectedOperationRegion.value.id);
    if (!previousRegion) return;
    previousRegion.polygon.setStyle(regionPolygonOptions(previousRegion.region_type, false));
    previousRegion.selected = false;
  }
}

async function selectRegion(region, { goTo = true, redirect = true } = {}) {
  if (!region) return selectNoRegion({ goTo });
  if (selectingObject && goTo) return;
  selectingObject = true;

  showRegions.value = false;
  deselectRegion();

  let newRegion = operationRegionMap.value.get(region.id);
  newRegion.polygon.setStyle(regionPolygonOptions(newRegion.region_type, true));
  selectedOperationRegion.value = newRegion;

  if (goTo) map.value.goToObject(newRegion.polygon);

  if (selectedOperationIssue.value) {
    if (!booleanContains(region.geoJSON, selectedOperationIssue.value.geoJSON)) {
      deselectIssue();
    } else {
      await nextTick();
      scrollToIssue(selectedOperationIssue.value, { behavior: 'smooth', block: 'top', force: true });
    }
  }

  if (redirect) {
    await router.replace({ query: { regionId: region.id, issueId: selectedOperationIssue.value?.id } });
  }

  await nextTick();
  await refreshView();
  await new Promise(resolve => {
    newRegion.selected = true;
    setTimeout(() => {
      selectingObject = false;
      resolve();
    }, 0);
  });
}

function selectRegionOnMap(polygon) {
  if (polygon.region) {
    selectRegion(polygon.region);
  }
}

async function selectNoRegion({ goTo = true } = {}) {
  if (selectingObject && goTo) return;
  doSelect();

  router.replace({ query: { regionId: null } });

  showRegions.value = false;
  deselectRegion();

  selectedOperationRegion.value = null;
  await refreshView();
}

async function selectIssueOnMap(marker) {
  doSelect();

  operationView.value = 'map';
  await selectIssue(marker.issue);
}

async function selectIssue(issue, { redirect = true } = {}) {
  deselectIssue();
  let newIssue = operationIssueMap.value.get(issue?.id);
  if (!newIssue) return;
  newIssue.marker.setIcon(SELECTED_RISK_ICONS[newIssue.riskLevel]);
  newIssue.marker.setZIndexOffset(newIssue.zIndexOffset + 1000);
  selectedOperationIssue.value = newIssue;

  if (selectedOperationRegion.value && !booleanContains(selectedOperationRegion.value.geoJSON, issue.geoJSON)) {
    await selectSmallestEnclosingRegion(issue);
  }

  if (redirect) {
    router.replace({ query: { regionId: selectedOperationRegion.value?.id, issueId: issue.id } });
  }

  if (issue?.latLng[0] && issue?.latLng[1]) map.value.enableCrosshair(issue.latLng[0], issue.latLng[1]);

  await nextTick();
  map.value.goToObject(newIssue.marker);
  scrollToIssue(newIssue, { behavior: 'smooth', block: 'top', force: true });
}

async function selectSmallestEnclosingRegion(issue) {
  let containingRegions = [...operationRegionMap.value.values()]
    .filter(region => booleanContains(region.geoJSON, issue.geoJSON))
    .sort(compareByAreaAndIdInverse);
  if (containingRegions.length > 0) {
    await selectRegion(containingRegions[0], { goTo: false });
  } else {
    selectNoRegion({ goTo: false });
  }
}

function deselectIssue() {
  if (selectedOperationIssue.value) {
    let previousIssue = operationIssueMap.value.get(selectedOperationIssue.value.id);
    previousIssue.marker.setIcon(RISK_ICONS[previousIssue.riskLevel]);
    previousIssue.marker.setZIndexOffset(previousIssue.zIndexOffset);
    selectedOperationIssue.value = null;
  }
}

function scrollToIssue(issue, options = {}) {
  const scrollToIndex = issue ? operationIssueList.value.indexOf(issue) : 0;
  const issueCardElement = issueScrollView.value.$el.querySelector('.issue-card');
  const elementHeight = issueCardElement?.style?.height?.replace('rem', '');
  const elementOffset = ((parseFloat(elementHeight) * 16) + 2) * scrollToIndex; // 1rem = 16px
  options.top = elementOffset;
  setTimeout(() => {
    issueScrollView.value?.$el?.scrollTo(options);
  }, 300);
}

function isIncluded(list, name) {
  return list.includes(name);
}
</script>
